/**
 * RunDetailPage — full-page run detail view
 *
 * Route: /runs/:runId
 *
 * Replaces the DetailDrawer sidebar. Loads all run data (scores, demographics,
 * respondents) and presents them in a full-page tabbed layout.
 */

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth, isClientAdmin, isClutchRole } from '../context/AuthContext'
import { apiFetch } from '../lib/apiClient'
import styles from './RunDetailPage.module.css'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RunRecord {
  run_id:           number
  run_label:        string
  run_date:         string
  engagement_name:  string
  client_name:      string
  instrument_code:  string
  respondent_count: number
  seat_limit:       number | null
  avg_readiness:    number | null
  avg_resistance:   number | null
  avg_index:        number | null
}

interface DimResult {
  dimension_code:   string
  dimension_name:   string
  avg_readiness:    number | null
  avg_resistance:   number | null
  avg_mechanics:    number | null
  avg_outcomes:     number | null
  avg_overall:      number | null
  n:                number
}

interface RunResults {
  run_id:             number
  instrument_code:    string
  engagement_name:    string
  run_label:          string
  run_date:           string
  respondent_count:   number
  dims:               DimResult[]
  overall:            DimResult | null
  suppressed:         boolean
  suppression_reason: string | null
}

interface RespondentRecord {
  respondent_id: string
  run_id:        number
  token:         string
  department:    string | null
  team:          string | null
  tenure_band:   string | null
  submitted_at:  string | null
  share_url:     string | null
}

interface RespondentSummary {
  run_id:         number
  total:          number
  completed:      number
  pending:        number
  completion_pct: number
}

interface DemographicSlice {
  segment:        string
  n:              number
  avg_overall:    number | null
  avg_readiness:  number | null
  avg_resistance: number | null
  avg_index:      number | null
  suppressed:     boolean
}

interface DemographicBreakdown {
  run_id:               number
  instrument_code:      string
  by_department:        DemographicSlice[]
  by_team:              DemographicSlice[]
  by_tenure:            DemographicSlice[]
  suppression_threshold: number
}

// Outreach / Email types
interface ContactOut {
  respondent_id:    string
  token:            string
  first_name:       string | null
  last_name:        string | null
  email:            string
  department:       string | null
  team:             string | null
  tenure:           string | null
  submitted_at:     string | null
  invite_status:    string | null
  invite_sent_at:   string | null
  reminder1_status: string | null
  reminder2_status: string | null
}

interface BulkUploadResult {
  created:  number
  skipped:  number
  errors:   string[]
  contacts: ContactOut[]
}

interface SendInvitesResult {
  sent:    number
  queued:  number
  skipped: number
  errors:  number
}

interface DripConfigOut {
  run_id:         number
  enabled:        boolean
  day3_enabled:   boolean
  day7_enabled:   boolean
  invite_sent_at: string | null
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchRuns(idToken: string | null): Promise<RunRecord[]> {
  return apiFetch<RunRecord[]>('/dashboard/runs', idToken)
}

async function fetchRunResults(runId: number, idToken: string | null): Promise<RunResults> {
  return apiFetch<RunResults>(`/runs/${runId}/results`, idToken)
}

async function fetchRespondents(runId: number, idToken: string | null): Promise<RespondentRecord[]> {
  return apiFetch<RespondentRecord[]>(`/admin/runs/${runId}/respondents`, idToken)
}

async function fetchRespondentSummary(runId: number, idToken: string | null): Promise<RespondentSummary> {
  return apiFetch<RespondentSummary>(`/admin/runs/${runId}/respondents/summary`, idToken)
}

async function fetchDemographics(runId: number, idToken: string | null): Promise<DemographicBreakdown> {
  return apiFetch<DemographicBreakdown>(`/runs/${runId}/demographics`, idToken)
}

async function addContactSingle(
  runId: number,
  payload: {
    first_name?: string
    last_name?: string
    email: string
    department?: string
    team?: string
    tenure_band?: string
  },
  idToken: string | null,
): Promise<ContactOut> {
  return apiFetch<ContactOut>(`/admin/runs/${runId}/contacts`, idToken, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

async function fetchContacts(runId: number, idToken: string | null): Promise<ContactOut[]> {
  return apiFetch<ContactOut[]>(`/admin/runs/${runId}/contacts`, idToken)
}

async function uploadContactsCsv(
  runId: number,
  file: File,
  idToken: string | null,
): Promise<BulkUploadResult> {
  const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'
  const form = new FormData()
  form.append('file', file)
  const headers: Record<string, string> = {}
  if (idToken) headers['Authorization'] = `Bearer ${idToken}`
  const res = await fetch(`${API_BASE}/admin/runs/${runId}/contacts/bulk-upload`, {
    method: 'POST',
    headers,
    body: form,
  })
  if (!res.ok) throw new Error(`Upload failed (${res.status}): ${await res.text()}`)
  return res.json()
}

async function sendInvites(runId: number, idToken: string | null): Promise<SendInvitesResult> {
  return apiFetch<SendInvitesResult>(`/admin/runs/${runId}/contacts/send-invites`, idToken, {
    method: 'POST',
  })
}

async function fetchDripConfig(runId: number, idToken: string | null): Promise<DripConfigOut> {
  return apiFetch<DripConfigOut>(`/admin/runs/${runId}/drip`, idToken)
}

async function setDripConfig(
  runId: number,
  cfg: { enabled: boolean; day3_enabled: boolean; day7_enabled: boolean },
  idToken: string | null,
): Promise<DripConfigOut> {
  return apiFetch<DripConfigOut>(`/admin/runs/${runId}/drip`, idToken, {
    method: 'POST',
    body: JSON.stringify(cfg),
  })
}

// ---------------------------------------------------------------------------
// Remediation content
// ---------------------------------------------------------------------------

type RemBand = 'needs' | 'developing' | 'strong'

interface RemEntry {
  label:      string
  needs:      string[]
  developing: string[]
  strong:     string[]
}

const CODEC_REM: Record<string, RemEntry> = {
  C: {
    label: 'Curiosity',
    needs: [
      'Launch a structured "What does this mean for me?" briefing by role so employees understand the personal impact before they can engage with it intellectually.',
      'Replace broadcast announcements with inquiry-based sessions — ask employees what questions they have rather than telling them what they need to know.',
      'Identify two or three highly curious informal leaders on each team and task them with surfacing peer questions to leadership.',
    ],
    developing: [
      'Introduce a monthly change Q&A session where leadership responds publicly to the most-submitted questions, building visible two-way inquiry.',
      'Create a searchable change knowledge base so curious employees can explore on their own schedule.',
      'Celebrate question-asking publicly — recognize employees who surface good challenges to assumptions.',
    ],
    strong: [
      'Transition curious employees into formal change scouts who surface early signals of confusion or resistance.',
      'Leverage high-curiosity individuals to design peer learning and mentorship for lower-engagement groups.',
      'Redirect curiosity into innovation: create a lightweight process for employees to submit workflow improvement ideas tied to the change.',
    ],
  },
  O: {
    label: 'Optimism',
    needs: [
      'Conduct an executive listening tour specifically to surface and acknowledge the root causes of pessimism — not to correct it, but to validate it publicly.',
      'Share concrete early wins, even small ones. Narrative evidence outperforms data when optimism is low.',
      'Identify and address the two or three most-cited concerns driving pessimism before optimism can improve.',
    ],
    developing: [
      'Formalize a "progress story" cadence: a monthly communication specifically highlighting what has worked and why.',
      'Introduce peer testimonials from employees who have had positive early experiences with the change.',
      'Set achievable short-term milestones that give employees visible reasons to believe success is possible.',
    ],
    strong: [
      'Deploy optimistic employees as change ambassadors in teams where pessimism is still high.',
      'Use optimism as a strategic asset — involve these employees in co-designing the next phase of the change.',
      'Document and share what drove this optimism so future initiatives can replicate it.',
    ],
  },
  D: {
    label: 'Drive',
    needs: [
      'Map the personal benefit of the change for each major role group — employees must see what is in it for them before drive will emerge.',
      'Remove structural barriers that make engagement feel futile: if effort leads nowhere, drive collapses.',
      'Engage direct managers as motivation partners — low drive is often a management culture problem as much as an individual one.',
    ],
    developing: [
      'Introduce clear, visible contribution opportunities so motivated employees can do more than the minimum.',
      'Create change-related stretch assignments or pilot roles for high-drive employees.',
      'Ensure drive is recognized publicly and promptly — delayed recognition has minimal motivational impact.',
    ],
    strong: [
      'Formalize high-drive employees as change leads with defined roles and accountability.',
      'Use this cohort to set a high standard and pull peers forward through proximity effect.',
      'Protect and sustain drive by delivering on the change promises that motivated it.',
    ],
  },
  E: {
    label: 'Evolve',
    needs: [
      'Reduce the perception of loss: explicitly acknowledge what will end and create a structured ceremony for closing the old way before opening the new.',
      'Provide psychological safety structures — managers must model willingness to change before frontline staff will follow.',
      'Start with micro-adaptation experiments in low-stakes contexts to build adaptive muscle before full deployment.',
    ],
    developing: [
      'Introduce structured after-action reviews that normalize learning from failure as part of the change.',
      'Pair low-adaptability employees with high-adaptability peers for supported behavior change.',
      'Create a clear path from the old way to the new — employees adapt faster when the journey is mapped.',
    ],
    strong: [
      'Leverage adaptive employees as change accelerators in the most resistant areas.',
      'Build an adaptability culture framework that can scale to future initiatives without starting from zero.',
      'Invest in these employees\' continued growth — high adaptability is a competitive organizational asset.',
    ],
  },
  CN: {
    label: 'Connection',
    needs: [
      'Conduct immediate trust repair: if connection scores are low, something has eroded trust — identify it specifically before any other intervention will work.',
      'Increase manager visibility and approachability around the change: scheduled one-on-ones with explicit change discussion time.',
      'Rebuild transparency: publish all known information about the change (including what is not yet decided) to signal honesty.',
    ],
    developing: [
      'Create structured peer connection moments around the change: small-group cross-functional conversations.',
      'Introduce a "we\'re in this together" communication track that highlights shared challenges and collective progress.',
      'Ensure managers consistently communicate that they are experiencing the change too, not just overseeing it.',
    ],
    strong: [
      'Formalize the connected cohort as a social infrastructure for future changes.',
      'Invest in cross-team connection to scale the trust network beyond existing relationships.',
      'Use this cohort as a resilience buffer when the change hits friction — high-trust networks absorb disruption better.',
    ],
  },
}

const HFA_REM: Record<string, RemEntry> = {
  EE: {
    label: 'Enablement & Environment',
    needs: [
      'Conduct a workflow audit before any other intervention: identify where the behavior sits in the day and what structural barriers prevent it.',
      'Protect time explicitly — build the behavior into meeting rhythms or daily schedule blocks rather than leaving it as "find time when you can."',
      'Run a tool access audit with real users. Permissions gaps, slow system response, and interface friction all count as environmental barriers.',
    ],
    developing: [
      'Address the two highest-friction workflow integration points — these have outsized impact on behavior completion.',
      'Build a "quick help" resource (job aid, peer buddy, in-system tip) accessible at the moment of behavior.',
      'Survey employees monthly on what still feels hard — remove friction iteratively rather than assuming the environment is good enough.',
    ],
    strong: [
      'Document what made the environment supportive and use it as a template for onboarding new staff into the habit.',
      'Advance from environmental support to behavioral automaticity — transition interventions from setup to sustainment.',
    ],
  },
  PR: {
    label: 'Planning & Routine Design',
    needs: [
      'Run an if-then planning exercise with every employee: "I will perform [behavior] at [time] immediately after [existing routine]." Specificity is the intervention.',
      'Provide implementation intention templates at the point of training, not as optional extras. Pre-loaded plans beat good intentions every time.',
      'Reduce startup friction: pre-stage everything the employee needs before the behavior begins so the cost of starting is nearly zero.',
    ],
    developing: [
      'Anchor the behavior to a specific stable routine through habit stacking — vague anchors (e.g., "in the morning") fail faster than specific ones.',
      'Review implementation intentions in manager one-on-ones during the first 30 days to refine and troubleshoot.',
      'Introduce a weekly plan-review moment where employees explicitly confirm whether their plan worked and adjust if not.',
    ],
    strong: [
      'Transition from explicit planning to automaticity: the sign of a mature habit plan is that you no longer need to reference it.',
      'Share successful routine designs across the team so high-performing employees\' systems can benefit peers.',
    ],
  },
  CT: {
    label: 'Cues & Triggers',
    needs: [
      'Install a single reliable environmental cue tied to the exact time and location of the behavior — spatial and temporal cues are the strongest.',
      'Eliminate competing cues in the same environment: if other prompts are louder, the habit cue will be ignored.',
      'If digital reminders are used, ensure they appear at the moment of action, not 10 minutes before — proximity is everything.',
    ],
    developing: [
      'Evaluate cue effectiveness monthly: if a cue is being ignored, replace it rather than reinforcing it.',
      'Build a redundant cue system: two complementary triggers reduce the chance of the behavior being missed.',
      'Involve employees in designing their own cues — self-chosen cues are 40% more effective than assigned ones.',
    ],
    strong: [
      'The cue is working; focus shifts to making the cue unnecessary as automaticity grows.',
      'Archive the cue design for onboarding new employees into the same habit.',
    ],
  },
  RF: {
    label: 'Reinforcement & Feedback',
    needs: [
      'Build immediate feedback loops into the behavior itself: completion confirmations, progress indicators, or manager acknowledgment within 24 hours.',
      'Train managers to deliver specific, behavior-referenced recognition rather than general praise ("you completed notes on time today" > "great work").',
      'Design milestone recognition moments at 30, 60, and 90 days to interrupt behavioral drift at its most common failure points.',
    ],
    developing: [
      'Transition from external reinforcement to internal progress tracking: help employees see their own improvement over time.',
      'Reduce dependence on manager recognition by building peer-to-peer recognition moments into team routines.',
      'Introduce streak-based visual tracking so employees can self-monitor consistency.',
    ],
    strong: [
      'Shift from performance recognition to mastery recognition: acknowledge quality and consistency, not just completion.',
      'Allow high performers to design reinforcement systems for newer adopters — lateral influence compounds faster than top-down recognition.',
    ],
  },
  BO: {
    label: 'Behavioral Outcomes',
    needs: [
      'The behavior is not yet habitual — shift the success metric from "did they do it" to "how consistent were they" to build the repetition foundation.',
      'Install a recovery ritual: when the behavior is missed, make it explicit that returning at the next opportunity is the expected response, not starting over.',
      'Reduce quality expectations temporarily to reduce the activation cost. Completion at lower quality builds consistency faster than perfection at low frequency.',
    ],
    developing: [
      'Track automaticity indicators: the behavior requires less deliberate decision-making, interruptions cause less disruption, and resumption after a break is faster.',
      'Audit the quality gap: if frequency is high but quality is low, the issue is calibration, not motivation. Provide examples of what good looks like.',
      'Set a 66-day target: habit formation averages 66 days of consistent repetition. Employees in this band are on track — sustain the environment.',
    ],
    strong: [
      'The behavior is habitual. Shift focus to habit maintenance during high-stress periods when routines collapse.',
      'Use this cohort to calibrate the definition of quality: what high-performing completers do becomes the standard for lower-performing groups.',
      'Document the adoption path so this cohort\'s journey can accelerate the next group of adopters.',
    ],
  },
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type Band = 'strong' | 'developing' | 'needs'

function scoreToBand(score: number | null): Band | null {
  if (score === null) return null
  if (score >= 75)    return 'strong'
  if (score >= 50)    return 'developing'
  return 'needs'
}

function fmt(n: number | null) { return n !== null ? `${Math.round(n)}%` : '—' }

function formatDate(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function dimPrimaryScore(dim: DimResult, isCodecInstr: boolean): number | null {
  if (isCodecInstr) return dim.avg_readiness
  if (dim.dimension_code === 'BO') return dim.avg_outcomes
  return dim.avg_mechanics ?? dim.avg_overall
}

// ---------------------------------------------------------------------------
// Shared micro-components
// ---------------------------------------------------------------------------

const BAND_LABELS: Record<Band, string> = {
  strong:     'Strong',
  developing: 'Developing',
  needs:      'Needs Support',
}

function bandClass(band: Band) {
  return band === 'strong'     ? styles.bandStrong
    : band === 'developing'    ? styles.bandDeveloping
    : styles.bandNeeds
}

function dimBandClass(band: Band | null) {
  if (band === 'strong')     return styles.dimBandStrong
  if (band === 'developing') return styles.dimBandDeveloping
  if (band === 'needs')      return styles.dimBandNeeds
  return ''
}

function BandPill({ band }: { band: Band }) {
  return (
    <span className={`${styles.bandPill} ${bandClass(band)}`}>
      {BAND_LABELS[band]}
    </span>
  )
}

function InstrumentTag({ code }: { code: string }) {
  const codec = code === 'CODEC-v3'
  return (
    <span className={`${styles.instrTag} ${codec ? styles.instrCodec : styles.instrHfa}`}>
      {codec ? 'CODE-C' : 'HFA'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// DimRow — dimension score + expandable remediation steps
// ---------------------------------------------------------------------------

function DimRow({ dim, isCodecInstr, expanded, onToggle }: {
  dim:          DimResult
  isCodecInstr: boolean
  expanded:     boolean
  onToggle:     () => void
}) {
  const score    = dimPrimaryScore(dim, isCodecInstr)
  const band     = scoreToBand(score)
  const remMap   = isCodecInstr ? CODEC_REM : HFA_REM
  const rem      = remMap[dim.dimension_code]
  const remSteps = band && rem ? rem[band as RemBand] : null

  const bandLabel = band === 'strong'     ? 'Strength'
    : band === 'developing' ? 'Opportunity'
    : band === 'needs'      ? 'Priority gap'
    : null

  return (
    <div className={styles.dimRow}>
      <button className={styles.dimToggle} onClick={onToggle} aria-expanded={expanded}>
        <div className={styles.dimInfo}>
          <span className={styles.dimName}>{rem?.label ?? dim.dimension_name}</span>
          {bandLabel && (
            <span className={`${styles.dimBandChip} ${dimBandClass(band)}`}>{bandLabel}</span>
          )}
        </div>
        <div className={styles.dimRight}>
          <span className={`${styles.dimScore} ${dimBandClass(band)}`}>
            {score !== null ? `${Math.round(score)}%` : '—'}
          </span>
          <span className={styles.dimChevron}>{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {score !== null && (
        <div className={styles.dimBarWrap}>
          <div className={`${styles.dimBarFill} ${dimBandClass(band)}`}
            style={{ width: `${Math.min(score, 100)}%` }} />
        </div>
      )}

      {isCodecInstr && dim.avg_resistance !== null && (
        <div className={styles.dimSubBar}>
          <span className={styles.dimSubLabel}>Resistance</span>
          <div className={styles.dimBarWrap}>
            <div className={styles.dimBarFillMagenta}
              style={{ width: `${Math.min(dim.avg_resistance, 100)}%` }} />
          </div>
          <span className={styles.dimSubScore}>{Math.round(dim.avg_resistance)}%</span>
        </div>
      )}

      {expanded && remSteps && (
        <div className={styles.remPanel}>
          <div className={`${styles.remPanelLabel} ${dimBandClass(band)}`}>
            {band === 'needs'      ? '🔴 Priority interventions'
              : band === 'developing' ? '🟡 Acceleration steps'
              : '🟢 Sustainment strategies'}
          </div>
          {remSteps.map((step, i) => (
            <div key={i} className={styles.remItem}>
              <div className={`${styles.remDot} ${dimBandClass(band)}`}>{i + 1}</div>
              <p>{step}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DemographicsPanel
// ---------------------------------------------------------------------------

type DemoView = 'department' | 'team' | 'tenure'

function DemographicsPanel({ runId, isCodec, idToken }: { runId: number; isCodec: boolean; idToken: string | null }) {
  const [data,    setData]    = useState<DemographicBreakdown | null>(null)
  const [loading, setLoading] = useState(true)
  const [err,     setErr]     = useState('')
  const [view,    setView]    = useState<DemoView>('department')

  useEffect(() => {
    setLoading(true)
    fetchDemographics(runId, idToken)
      .then(d => { setData(d); setLoading(false) })
      .catch(e => { setErr(e instanceof Error ? e.message : 'Error'); setLoading(false) })
  }, [runId, idToken])

  if (loading) return (
    <div className={styles.panelLoading}>
      <div className={styles.spinnerSm} /><span>Loading demographics…</span>
    </div>
  )
  if (err)    return <p className={styles.errNote}>Could not load demographic data: {err}</p>
  if (!data)  return null

  const slices = view === 'department' ? data.by_department
    : view === 'team'  ? data.by_team
    : data.by_tenure

  return (
    <div>
      <div className={styles.demoTabGroup}>
        {(['department', 'team', 'tenure'] as DemoView[]).map(v => (
          <button key={v}
            className={`${styles.demoTabBtn} ${view === v ? styles.demoTabActive : ''}`}
            onClick={() => setView(v)}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </button>
        ))}
      </div>

      {slices.length === 0 ? (
        <p className={styles.noDataNote}>
          No demographic data available. Pre-register respondents with department, team,
          or tenure info to enable breakdowns.
        </p>
      ) : (
        <div className={styles.demoRows}>
          {slices.map(s => (
            <div key={s.segment} className={styles.demoRow}>
              <span className={styles.demoRowLabel} title={s.segment}>{s.segment}</span>
              {s.suppressed ? (
                <>
                  <div className={styles.demoBarWrap} />
                  <span className={styles.demoSuppressed}>Not enough responses</span>
                </>
              ) : (
                <>
                  <div className={styles.demoBarWrap}>
                    <div className={styles.demoBarFill}
                      style={{ width: `${(s.avg_overall ?? 0)}%` }} />
                  </div>
                  <span className={styles.demoRowScore}>
                    {s.avg_overall !== null ? Math.round(s.avg_overall) : '—'}
                  </span>
                  <span className={styles.demoRowN}>n={s.n}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {isCodec && slices.some(s => !s.suppressed && s.avg_resistance !== null) && (
        <div style={{ marginTop: 24 }}>
          <p className={styles.demoSectionTitle}>Resistance by {view}</p>
          <div className={styles.demoRows}>
            {slices.filter(s => !s.suppressed).map(s => (
              <div key={s.segment} className={styles.demoRow}>
                <span className={styles.demoRowLabel} title={s.segment}>{s.segment}</span>
                <div className={styles.demoBarWrap}>
                  <div className={`${styles.demoBarFill} ${styles.demoBarFillMagenta}`}
                    style={{ width: `${s.avg_resistance ?? 0}%` }} />
                </div>
                <span className={styles.demoRowScore}>
                  {s.avg_resistance !== null ? Math.round(s.avg_resistance) : '—'}
                </span>
                <span className={styles.demoRowN}>n={s.n}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// RespondentsPanel
// ---------------------------------------------------------------------------

const TENURE_OPTIONS = ['<1 yr', '1-3 yr', '3-5 yr', '5-10 yr', '10+ yr']

function RespondentsPanel({ runId, idToken }: { runId: number; idToken: string | null }) {
  const [summary,  setSummary]  = useState<RespondentSummary | null>(null)
  const [list,     setList]     = useState<RespondentRecord[]>([])
  const [loading,  setLoading]  = useState(true)
  const [loadErr,  setLoadErr]  = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  async function load() {
    setLoading(true); setLoadErr('')
    try {
      const [sum, lst] = await Promise.all([
        fetchRespondentSummary(runId, idToken),
        fetchRespondents(runId, idToken),
      ])
      setSummary(sum); setList(lst)
    } catch (e: unknown) {
      setLoadErr(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [runId, idToken]) // eslint-disable-line react-hooks/exhaustive-deps

  function copyLink(url: string, id: string) {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1800)
    })
  }

  if (loading) return (
    <div className={styles.panelLoading}>
      <div className={styles.spinnerSm} /><span>Loading respondents…</span>
    </div>
  )
  if (loadErr) return <p className={styles.errNote}>Could not load respondents: {loadErr}</p>

  const pct = summary ? Math.round(summary.completion_pct) : 0

  return (
    <div>
      {summary && (
        <div className={styles.rateCard}>
          <div className={styles.rateNumbers}>
            <span className={styles.rateMain}>{pct}%</span>
            <span className={styles.rateSub}>Response rate</span>
          </div>
          <div className={styles.rateTrack}>
            <div className={styles.rateFill} style={{ width: `${pct}%` }} />
          </div>
          <span className={styles.rateDetail}>{summary.completed} / {summary.total} complete</span>
        </div>
      )}

      {list.length === 0 ? (
        <p className={styles.noDataNote}>
          No pre-registered respondents yet. Add one below to generate individual share links.
        </p>
      ) : (
        <div className={styles.respondentList}>
          {list.map(r => {
            const complete   = !!r.submitted_at
            const displayId  = r.department
              ? `${r.respondent_id.slice(0, 8)}… · ${r.department}`
              : r.respondent_id.slice(0, 12) + '…'
            const detail = [r.team, r.tenure_band].filter(Boolean).join(' · ')
            return (
              <div key={r.token} className={styles.respondentRow}>
                <div className={`${styles.respondentStatus} ${complete ? styles.statusComplete : styles.statusPending}`} />
                <div className={styles.respondentMeta}>
                  <div className={styles.respondentId}>{displayId}</div>
                  {detail && <div className={styles.respondentDetail}>{detail}</div>}
                </div>
                <span className={`${styles.respondentStatusLabel} ${complete ? styles.statusLabelComplete : styles.statusLabelPending}`}>
                  {complete ? 'Complete' : 'Pending'}
                </span>
                {r.token && (
                  <button
                    className={`${styles.copyLinkBtn} ${copiedId === r.token ? styles.copied : ''}`}
                    onClick={() => copyLink(`${window.location.origin}/r/${r.token}`, r.token)}>
                    {copiedId === r.token ? '✓' : 'Copy link'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

    </div>
  )
}

// ---------------------------------------------------------------------------
// OutreachPanel
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, string> = {
  queued:    'Queued',
  sent:      'Sent',
  delivered: 'Delivered',
  bounced:   'Bounced',
  failed:    'Failed',
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className={styles.statusNone}>—</span>
  return (
    <span className={`${styles.statusBadge} ${styles[`status_${status}`]}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  )
}

function OutreachPanel({
  runId, idToken, seatLimit, canEditSeats, onSeatLimitChange,
}: {
  runId:             number
  idToken:           string | null
  seatLimit:         number | null
  canEditSeats:      boolean
  onSeatLimitChange: (newLimit: number | null) => void
}) {
  const [contacts,   setContacts]   = useState<ContactOut[]>([])
  const [drip,       setDrip]       = useState<DripConfigOut | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [err,        setErr]        = useState('')
  const [dragging,   setDragging]   = useState(false)
  const [uploading,  setUploading]  = useState(false)
  const [uploadMsg,  setUploadMsg]  = useState('')
  const [uploadErr,  setUploadErr]  = useState('')
  const [sending,    setSending]    = useState(false)
  const [sendMsg,    setSendMsg]    = useState('')
  const [sendErr,    setSendErr]    = useState('')
  const [editingSeats,  setEditingSeats]  = useState(false)
  const [seatDraft,     setSeatDraft]     = useState('')
  const [savingSeats,   setSavingSeats]   = useState(false)
  const [seatErr,       setSeatErr]       = useState('')

  // Single-contact add form
  const [showAddForm, setShowAddForm] = useState(false)
  const [addFirst,   setAddFirst]     = useState('')
  const [addLast,    setAddLast]      = useState('')
  const [addEmail,   setAddEmail]     = useState('')
  const [addDept,    setAddDept]      = useState('')
  const [addTeam,    setAddTeam]      = useState('')
  const [addTenure,  setAddTenure]    = useState('')
  const [addSaving,  setAddSaving]    = useState(false)
  const [addMsg,     setAddMsg]       = useState('')
  const [addErr,     setAddErr]       = useState('')

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const appBase = window.location.origin

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchContacts(runId, idToken), fetchDripConfig(runId, idToken)])
      .then(([c, d]) => { setContacts(c); setDrip(d); setLoading(false) })
      .catch(e => { setErr(e instanceof Error ? e.message : 'Error'); setLoading(false) })
  }, [runId, idToken])

  async function handleFile(file: File) {
    if (!file.name.endsWith('.csv')) {
      setUploadErr('Please upload a CSV file.')
      return
    }
    setUploading(true)
    setUploadMsg('')
    setUploadErr('')
    try {
      const result = await uploadContactsCsv(runId, file, idToken)
      setContacts(result.contacts)
      const parts = [`${result.created} added`]
      if (result.skipped)       parts.push(`${result.skipped} skipped (already exist)`)
      if (result.errors.length) parts.push(`${result.errors.length} error(s)`)
      setUploadMsg(parts.join(' · '))
      if (result.errors.length) setUploadErr(result.errors.slice(0, 3).join('\n'))
    } catch (e) {
      setUploadErr(e instanceof Error ? e.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function handleSendInvites() {
    setSending(true)
    setSendMsg('')
    setSendErr('')
    try {
      const r = await sendInvites(runId, idToken)
      const parts = []
      if (r.sent)    parts.push(`${r.sent} sent`)
      if (r.queued)  parts.push(`${r.queued} queued (SendGrid not yet active)`)
      if (r.skipped) parts.push(`${r.skipped} already had invites`)
      if (r.errors)  parts.push(`${r.errors} failed`)
      setSendMsg(parts.join(' · ') || 'Done.')
      const refreshed = await fetchContacts(runId, idToken)
      setContacts(refreshed)
    } catch (e) {
      setSendErr(e instanceof Error ? e.message : 'Error sending invites')
    } finally {
      setSending(false)
    }
  }

  async function handleSaveSeatLimit() {
    const parsed = seatDraft.trim() === '' ? null : parseInt(seatDraft, 10)
    if (seatDraft.trim() !== '' && (isNaN(parsed!) || parsed! < 1)) {
      setSeatErr('Enter a positive number, or leave blank to remove the limit.')
      return
    }
    setSavingSeats(true); setSeatErr('')
    try {
      const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'https://clutch-ocm-scorer-uqrk7imnlq-uc.a.run.app'
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (idToken) headers['Authorization'] = `Bearer ${idToken}`
      const res = await fetch(`${API_BASE}/runs/${runId}/seat-limit`, {
        method: 'PATCH', headers, body: JSON.stringify({ seat_limit: parsed }),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      const data = await res.json()
      onSeatLimitChange(data.seat_limit)
      setEditingSeats(false)
    } catch (e) {
      setSeatErr(e instanceof Error ? e.message : 'Could not save seat limit.')
    } finally {
      setSavingSeats(false)
    }
  }

  async function handleDripToggle(key: 'enabled' | 'day3_enabled' | 'day7_enabled') {
    if (!drip) return
    const updated = { ...drip, [key]: !drip[key] }
    try {
      const result = await setDripConfig(runId, {
        enabled:      updated.enabled,
        day3_enabled: updated.day3_enabled,
        day7_enabled: updated.day7_enabled,
      }, idToken)
      setDrip(result)
    } catch (e) {
      // Silently revert — user sees no change
    }
  }

  async function handleAddContact(e: React.FormEvent) {
    e.preventDefault()
    setAddSaving(true); setAddErr(''); setAddMsg('')
    try {
      const newContact = await addContactSingle(runId, {
        first_name:  addFirst.trim()  || undefined,
        last_name:   addLast.trim()   || undefined,
        email:       addEmail.trim(),
        department:  addDept.trim()   || undefined,
        team:        addTeam.trim()   || undefined,
        tenure_band: addTenure        || undefined,
      }, idToken)
      setContacts(prev => [...prev, newContact])
      setAddMsg('Contact added.')
      setAddFirst(''); setAddLast(''); setAddEmail('')
      setAddDept('');  setAddTeam('');  setAddTenure('')
      setShowAddForm(false)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not add contact.'
      setAddErr(msg.includes('already exists') ? 'A contact with this email is already on this run.' : msg)
    } finally {
      setAddSaving(false)
    }
  }

  if (loading) return (
    <div className={styles.panelLoading}>
      <div className={styles.spinnerSm} /><span>Loading outreach…</span>
    </div>
  )
  if (err) return <p className={styles.errNote}>Could not load outreach data: {err}</p>

  const needsInvite  = contacts.filter(c => !c.invite_status).length
  const atSeatLimit  = seatLimit !== null && contacts.length >= seatLimit

  return (
    <div className={styles.outreachPanel}>

      {/* ── Seat limit editor (clutch staff only) ── */}
      {canEditSeats && (
        <section className={styles.outreachSection}>
          <div className={styles.outreachSectionTitle}>Seat limit</div>
          {!editingSeats ? (
            <div className={styles.seatLimitRow}>
              <span className={styles.seatLimitValue}>
                {seatLimit !== null
                  ? `${contacts.length} / ${seatLimit} seats used`
                  : 'No limit set'}
              </span>
              <button
                className={styles.seatEditBtn}
                onClick={() => { setSeatDraft(seatLimit !== null ? String(seatLimit) : ''); setEditingSeats(true) }}
              >
                {seatLimit !== null ? 'Edit' : 'Set limit'}
              </button>
            </div>
          ) : (
            <div className={styles.seatEditRow}>
              <input
                className={styles.seatEditInput}
                type="number"
                min="1"
                placeholder="e.g. 50"
                value={seatDraft}
                onChange={e => { setSeatDraft(e.target.value); setSeatErr('') }}
              />
              <button className={styles.seatSaveBtn} onClick={handleSaveSeatLimit} disabled={savingSeats}>
                {savingSeats ? 'Saving…' : 'Save'}
              </button>
              <button className={styles.addCancelBtn} onClick={() => { setEditingSeats(false); setSeatErr('') }}>
                Cancel
              </button>
            </div>
          )}
          {seatErr && <p className={styles.outreachError}>{seatErr}</p>}
          {atSeatLimit && (
            <p className={styles.seatLimitWarning}>
              ⚠ Seat limit reached — sending is disabled. Increase or remove the limit to send more invitations.
            </p>
          )}
        </section>
      )}

      {/* ── Add single contact ── */}
      <section className={styles.outreachSection}>
        <div className={styles.outreachSectionHeader}>
          <div className={styles.outreachSectionTitle}>Add contact</div>
          {!showAddForm && (
            <button className={styles.addOneBtn} onClick={() => { setAddMsg(''); setAddErr(''); setShowAddForm(true) }}>
              + Add one person
            </button>
          )}
        </div>
        {addMsg && !showAddForm && <p className={styles.outreachSuccess}>{addMsg}</p>}
        {showAddForm && (
          <form className={styles.addOneForm} onSubmit={handleAddContact}>
            <div className={styles.addOneRow}>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>First name</label>
                <input className={styles.addOneInput} value={addFirst}
                  onChange={e => setAddFirst(e.target.value)} placeholder="Jane" />
              </div>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>Last name</label>
                <input className={styles.addOneInput} value={addLast}
                  onChange={e => setAddLast(e.target.value)} placeholder="Smith" />
              </div>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>Email *</label>
                <input className={styles.addOneInput} type="email" required value={addEmail}
                  onChange={e => setAddEmail(e.target.value)} placeholder="jane@org.gov" />
              </div>
            </div>
            <div className={styles.addOneRow}>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>Department</label>
                <input className={styles.addOneInput} value={addDept}
                  onChange={e => setAddDept(e.target.value)} placeholder="Finance" />
              </div>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>Team</label>
                <input className={styles.addOneInput} value={addTeam}
                  onChange={e => setAddTeam(e.target.value)} placeholder="Accounts Payable" />
              </div>
              <div className={styles.addOneField}>
                <label className={styles.addOneLabel}>Tenure</label>
                <select className={styles.addOneSelect} value={addTenure}
                  onChange={e => setAddTenure(e.target.value)}>
                  <option value="">Select…</option>
                  {TENURE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            {addErr && <p className={styles.outreachError}>{addErr}</p>}
            <div className={styles.addOneActions}>
              <button className={styles.addSubmitBtn} type="submit" disabled={addSaving}>
                {addSaving ? 'Adding…' : 'Add contact'}
              </button>
              <button className={styles.addCancelBtn} type="button"
                onClick={() => { setShowAddForm(false); setAddErr('') }}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </section>

      {/* ── CSV Upload Zone ── */}
      <section className={styles.outreachSection}>
        <div className={styles.outreachSectionTitle}>Bulk import</div>
        <p className={styles.outreachHint}>
          Upload a CSV with columns: <code>first_name</code>, <code>last_name</code>, <code>email</code>
          (optional: <code>department</code>, <code>team</code>, <code>tenure</code>).
          Existing emails are skipped automatically.
        </p>

        <div
          className={`${styles.dropZone} ${dragging ? styles.dropZoneDragging : ''}`}
          onDragOver={e => { e.preventDefault(); setDragging(true) }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault()
            setDragging(false)
            const f = e.dataTransfer.files[0]
            if (f) handleFile(f)
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
          />
          {uploading
            ? <span className={styles.dropZoneText}>Uploading…</span>
            : <span className={styles.dropZoneText}>
                Drop CSV here or <span className={styles.dropZoneLink}>click to browse</span>
              </span>
          }
        </div>

        {uploadMsg && <p className={styles.outreachSuccess}>{uploadMsg}</p>}
        {uploadErr && <p className={styles.outreachError}>{uploadErr}</p>}
      </section>

      {/* ── Send Invites ── */}
      {contacts.length > 0 && (
        <section className={styles.outreachSection}>
          <div className={styles.outreachSectionTitle}>Send invitations</div>
          <div className={styles.sendInvitesRow}>
            <button
              className={styles.sendInvitesBtn}
              onClick={handleSendInvites}
              disabled={sending || needsInvite === 0 || atSeatLimit}
              title={atSeatLimit ? 'Seat limit reached' : undefined}
            >
              {sending ? 'Sending…' : `Send invites (${needsInvite} pending)`}
            </button>
            {needsInvite === 0 && !atSeatLimit && (
              <span className={styles.outreachHint}>All contacts have been invited.</span>
            )}
            {atSeatLimit && (
              <span className={styles.seatLimitWarning}>Seat limit reached.</span>
            )}
          </div>
          {sendMsg && <p className={styles.outreachSuccess}>{sendMsg}</p>}
          {sendErr && <p className={styles.outreachError}>{sendErr}</p>}
        </section>
      )}

      {/* ── Drip Config ── */}
      {drip && (
        <section className={styles.outreachSection}>
          <div className={styles.outreachSectionTitle}>Drip reminders</div>
          <div className={styles.dripToggles}>
            <label className={styles.dripToggle}>
              <input
                type="checkbox"
                checked={drip.enabled}
                onChange={() => handleDripToggle('enabled')}
              />
              <span>Enable automated reminders</span>
            </label>
            {drip.enabled && (
              <>
                <label className={`${styles.dripToggle} ${styles.dripToggleIndent}`}>
                  <input
                    type="checkbox"
                    checked={drip.day3_enabled}
                    onChange={() => handleDripToggle('day3_enabled')}
                  />
                  <span>Day 3 reminder</span>
                </label>
                <label className={`${styles.dripToggle} ${styles.dripToggleIndent}`}>
                  <input
                    type="checkbox"
                    checked={drip.day7_enabled}
                    onChange={() => handleDripToggle('day7_enabled')}
                  />
                  <span>Day 7 reminder</span>
                </label>
              </>
            )}
            {drip.invite_sent_at && (
              <p className={styles.outreachHint}>
                Initial invites sent {new Date(drip.invite_sent_at).toLocaleDateString()}.
                Reminders are sent only to respondents who haven't submitted.
              </p>
            )}
          </div>
        </section>
      )}

      {/* ── Contact List ── */}
      {contacts.length > 0 && (
        <section className={styles.outreachSection}>
          <div className={styles.outreachSectionTitle}>
            Contacts <span className={styles.outreachCount}>({contacts.length})</span>
          </div>
          <div className={styles.contactTableWrap}>
            <table className={styles.contactTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Dept / Team</th>
                  <th>Invite</th>
                  <th>Rem 1</th>
                  <th>Rem 2</th>
                  <th>Submitted</th>
                  <th>Link</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.respondent_id} className={c.submitted_at ? styles.contactRowDone : ''}>
                    <td>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                    <td className={styles.contactEmail}>{c.email}</td>
                    <td className={styles.contactMeta}>
                      {[c.department, c.team].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td><StatusBadge status={c.invite_status} /></td>
                    <td><StatusBadge status={c.reminder1_status} /></td>
                    <td><StatusBadge status={c.reminder2_status} /></td>
                    <td className={styles.contactSubmit}>
                      {c.submitted_at
                        ? new Date(c.submitted_at).toLocaleDateString()
                        : <span className={styles.statusNone}>Pending</span>}
                    </td>
                    <td>
                      <button
                        className={styles.contactLinkBtn}
                        title={`${appBase}/r/${c.token}`}
                        onClick={() => navigator.clipboard.writeText(`${appBase}/r/${c.token}`)}
                      >
                        Copy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {contacts.length === 0 && (
        <p className={styles.noDataNote}>
          No contacts yet. Upload a CSV to get started.
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// RunDetailPage
// ---------------------------------------------------------------------------

type DetailTab = 'scores' | 'demographics' | 'respondents' | 'outreach'

const VALID_TABS: DetailTab[] = ['scores', 'demographics', 'respondents', 'outreach']

export function RunDetailPage() {
  const { runId } = useParams<{ runId: string }>()
  const navigate  = useNavigate()
  const [searchParams] = useSearchParams()
  const id        = Number(runId)
  const { idToken, platformUser } = useAuth()
  const clientAdmin  = isClientAdmin(platformUser)
  const canEditSeats = isClutchRole(platformUser)

  // Honour ?tab= query param (e.g. from dashboard Outreach shortcut)
  const tabParam = searchParams.get('tab') as DetailTab | null
  const initialTab: DetailTab =
    tabParam && VALID_TABS.includes(tabParam) && !(clientAdmin && tabParam !== 'scores')
      ? tabParam
      : 'scores'

  const [run,       setRun]       = useState<RunRecord | null>(null)
  const [results,   setResults]   = useState<RunResults | null>(null)
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState('')
  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab)
  const [expandedDim, setExpandedDim] = useState<string | null>(null)

  const [seatLimit, setSeatLimit] = useState<number | null>(null)

  useEffect(() => {
    if (isNaN(id)) { setError('Invalid run ID'); setLoading(false); return }
    Promise.all([fetchRuns(idToken), fetchRunResults(id, idToken)])
      .then(([runs, res]) => {
        const match = runs.find(r => r.run_id === id)
        if (!match) { setError('Run not found'); setLoading(false); return }
        setRun(match)
        setSeatLimit(match.seat_limit ?? null)
        setResults(res)
        setLoading(false)
      })
      .catch(e => { setError(e instanceof Error ? e.message : 'Error'); setLoading(false) })
  }, [id])

  if (loading) {
    return (
      <div className={styles.stateScreen}>
        <div className={styles.spinner} /><p>Loading run…</p>
      </div>
    )
  }

  if (error || !run) {
    return (
      <div className={styles.stateScreen}>
        <p className={styles.errorMsg}>{error || 'Run not found'}</p>
        <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
      </div>
    )
  }

  const codec = run.instrument_code === 'CODEC-v3'
  const band  = scoreToBand(codec ? run.avg_readiness : run.avg_index)

  return (
    <div className={styles.page}>

      {/* Breadcrumb */}
      <button className={styles.backBtn} onClick={() => navigate('/dashboard')}>
        ← Dashboard
      </button>

      {/* Page header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerMeta}>
          <span className={styles.clientName}>{run.client_name}</span>
          <span className={styles.headerSep}>›</span>
          <span className={styles.engName}>{run.engagement_name}</span>
        </div>
        <div className={styles.headerMain}>
          <div className={styles.headerTitleRow}>
            <h1 className={styles.pageTitle}>{run.run_label}</h1>
            <InstrumentTag code={run.instrument_code} />
            {band && <BandPill band={band} />}
          </div>
          <div className={styles.headerSubRow}>
            {formatDate(run.run_date)}
            <span className={styles.dot}>·</span>
            {run.respondent_count} respondent{run.respondent_count !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Score tiles */}
      <div className={styles.scoreTiles}>
        {codec ? (
          <>
            <div className={styles.scoreTile} data-color="aqua">
              <div className={styles.tileValue}>{fmt(run.avg_readiness)}</div>
              <div className={styles.tileLabel}>Avg Readiness</div>
              {scoreToBand(run.avg_readiness) && <BandPill band={scoreToBand(run.avg_readiness)!} />}
            </div>
            <div className={styles.scoreTile} data-color="magenta">
              <div className={styles.tileValue}>{fmt(run.avg_resistance)}</div>
              <div className={styles.tileLabel}>Avg Resistance</div>
            </div>
          </>
        ) : (
          <div className={styles.scoreTile} data-color="aqua">
            <div className={styles.tileValue}>{fmt(run.avg_index)}</div>
            <div className={styles.tileLabel}>Habit Adoption Index</div>
            {scoreToBand(run.avg_index) && <BandPill band={scoreToBand(run.avg_index)!} />}
          </div>
        )}
      </div>

      {/* Tabs — Demographics, Respondents + Outreach hidden for Client Admin */}
      <div className={styles.tabs}>
        {(
          ['scores', ...(clientAdmin ? [] : ['demographics', 'respondents', 'outreach'])] as DetailTab[]
        ).map(t => (
          <button key={t}
            className={`${styles.tab} ${activeTab === t ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(t)}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className={styles.tabContent}>

        {/* ── Scores ── */}
        {activeTab === 'scores' && (
          <>
            {run.respondent_count === 0 && (
              <p className={styles.noDataNote}>
                No submissions yet. Share the link above to start collecting responses.
              </p>
            )}

            {results?.suppressed && (
              <div className={styles.suppressionNotice}>
                <span className={styles.suppressionIcon}>🔒</span>
                <p>{results.suppression_reason}</p>
              </div>
            )}

            {run.respondent_count > 0 && !results?.suppressed && results && (
              <div className={styles.dimSection}>
                <div className={styles.dimSectionLabel}>
                  Dimension breakdown
                  <span className={styles.dimSectionHint}>
                    (click any dimension for recommended next steps)
                  </span>
                </div>
                {results.dims.map(dim => (
                  <DimRow
                    key={dim.dimension_code}
                    dim={dim}
                    isCodecInstr={codec}
                    expanded={expandedDim === dim.dimension_code}
                    onToggle={() => setExpandedDim(prev =>
                      prev === dim.dimension_code ? null : dim.dimension_code
                    )}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Demographics ── */}
        {activeTab === 'demographics' && (
          <DemographicsPanel runId={id} isCodec={codec} idToken={idToken} />
        )}

        {/* ── Respondents ── (hidden for Client Admin) */}
        {activeTab === 'respondents' && !clientAdmin && (
          <RespondentsPanel runId={id} idToken={idToken} />
        )}

        {/* ── Outreach ── (hidden for Client Admin) */}
        {activeTab === 'outreach' && !clientAdmin && (
          <OutreachPanel
            runId={id}
            idToken={idToken}
            seatLimit={seatLimit}
            canEditSeats={canEditSeats}
            onSeatLimitChange={setSeatLimit}
          />
        )}
      </div>
    </div>
  )
}
